import React, { useState, useEffect } from 'react';
import api from '../api/axios';

/* ===========================================================================
 * UserNotice — Notificação estilo "push" configurável pelo admin
 * ---------------------------------------------------------------------------
 * - Busca as chaves userNoticeEnabled / userNoticeTitle / userNoticeMessage
 *   em /api/settings (configuradas em Admin → Configurações).
 * - Aparece só nas telas do cliente (é incluído manualmente em cada página,
 *   nunca no painel admin).
 * - Aparece sempre que o cliente acessa e, no mínimo, 1x a cada 12h — mesmo
 *   que ele já tenha fechado antes e continue logado/com o navegador aberto.
 *   Usa localStorage (não sessionStorage) pra sobreviver ao fechar o navegador.
 * ========================================================================= */

const LAST_SHOWN_KEY_PREFIX = 'comex_notice_last_shown_';
const REAPPEAR_INTERVAL_MS = 12 * 60 * 60 * 1000; // 12 horas

const UserNotice = () => {
  const [notice, setNotice] = useState(null);
  const [visible, setVisible] = useState(false);
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get('/settings')
      .then((r) => {
        if (cancelled) return;
        const enabled = r.data?.userNoticeEnabled === 'true';
        const title = (r.data?.userNoticeTitle || '').trim();
        const message = (r.data?.userNoticeMessage || '').trim();
        if (!enabled || !message) return;

        const storageKey = `${LAST_SHOWN_KEY_PREFIX}${title}::${message}`;
        const lastShown = parseInt(localStorage.getItem(storageKey), 10);
        const alreadyShownRecently = Number.isFinite(lastShown) && (Date.now() - lastShown) < REAPPEAR_INTERVAL_MS;
        if (alreadyShownRecently) return;

        setNotice({ title, message, storageKey });
        // Marca como "mostrado agora" — garante o intervalo de 12h mesmo que
        // o cliente navegue entre telas sem fechar o aviso manualmente.
        localStorage.setItem(storageKey, String(Date.now()));
        // pequeno delay pra entrar com a animação de slide-in
        setTimeout(() => setVisible(true), 400);
      })
      .catch(() => { /* silencioso — nunca deve quebrar a tela do usuário */ });
    return () => { cancelled = true; };
  }, []);

  const handleClose = () => {
    setClosing(true);
    setTimeout(() => setVisible(false), 250);
  };

  if (!notice || !visible) return null;

  return (
    <>
      <style>{`
        .un-toast {
          position: fixed; top: 20px; right: 20px; z-index: 5000;
          max-width: 380px; width: calc(100vw - 40px);
          background: #fff; border-radius: 16px;
          box-shadow: 0 16px 44px rgba(0,0,0,0.22);
          border: 1px solid rgba(255,153,0,0.20);
          overflow: hidden;
          animation: un-slide-in 0.35s cubic-bezier(.2,.9,.3,1.2);
          font-family: 'Lexend', sans-serif;
        }
        .un-toast.un-closing { animation: un-slide-out 0.25s ease forwards; }
        @keyframes un-slide-in {
          from { transform: translateX(120%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        @keyframes un-slide-out {
          from { transform: translateX(0); opacity: 1; }
          to { transform: translateX(120%); opacity: 0; }
        }
        .un-toast-bar { height: 4px; background: linear-gradient(90deg, #f90, #ff6600); }
        .un-toast-body { display: flex; gap: 12px; padding: 16px 16px 16px 14px; }
        .un-toast-icon {
          width: 38px; height: 38px; border-radius: 10px; flex-shrink: 0;
          background: linear-gradient(135deg, #f90, #ff6600); color: #fff;
          display: flex; align-items: center; justify-content: center; font-size: 16px;
          box-shadow: 0 4px 12px rgba(255,153,0,0.35);
        }
        .un-toast-content { flex: 1; min-width: 0; }
        .un-toast-title { font-size: 14px; font-weight: 800; color: #1a1a2e; margin-bottom: 3px; }
        .un-toast-message { font-size: 13px; color: #555; line-height: 1.5; white-space: pre-wrap; }
        .un-toast-close {
          background: none; border: none; cursor: pointer; color: #aaa;
          width: 24px; height: 24px; flex-shrink: 0; border-radius: 8px;
          display: flex; align-items: center; justify-content: center; transition: all 0.2s;
        }
        .un-toast-close:hover { background: #f6f7fb; color: #666; }
        @media (max-width: 480px) {
          .un-toast { top: 12px; right: 12px; left: 12px; max-width: none; width: auto; }
        }
      `}</style>
      <div className={`un-toast ${closing ? 'un-closing' : ''}`} role="alert">
        <div className="un-toast-bar" />
        <div className="un-toast-body">
          <div className="un-toast-icon"><i className="fas fa-bullhorn"></i></div>
          <div className="un-toast-content">
            {notice.title && <div className="un-toast-title">{notice.title}</div>}
            <div className="un-toast-message">{notice.message}</div>
          </div>
          <button className="un-toast-close" onClick={handleClose} aria-label="Fechar aviso">
            <i className="fas fa-times"></i>
          </button>
        </div>
      </div>
    </>
  );
};

export default UserNotice;
